import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ownErrorToastOptions } from '../interceptors/http-error.interceptor';
import { CreateTemplatePayload, MetaTemplate } from './meta-whatsapp.service';

/**
 * Integrações WhatsApp — N números por cliente (CU-17tm466mfzu). Backend: `/whatsapp-accounts`.
 * O `MetaWhatsappService` continua para as variáveis de lead e para a rota legada da principal.
 */
export type WhatsappAccountStatus = 'connected' | 'pending' | 'token_expired' | 'permission_denied' | 'disabled';

/**
 * Estado que a tela mostra: o status do backend mais o que só o detalhe sabe na hora (token
 * recusado como inválido, credencial salva e ainda não verificada).
 */
export type WhatsappConnectionState = WhatsappAccountStatus | 'token_invalid' | 'unverified';

/** Um rótulo por estado — selo da lista, selo do título e card de conexão falam igual. */
export const WHATSAPP_STATE_LABELS: { [state in WhatsappConnectionState]: string } = {
  connected: 'Conectada',
  pending: 'Pendente — faltam dados',
  unverified: 'Não verificada',
  token_expired: 'Token expirado',
  token_invalid: 'Token inválido',
  permission_denied: 'Sem permissão',
  disabled: 'Desativada',
};

export interface WhatsappAccount {
  id: string;
  name: string;
  status: WhatsappAccountStatus;
  waba_id?: string | null;
  phone_number_id?: string | null;
  display_phone_number?: string | null;
  verified_name?: string | null;
  verified_at?: string | null;
  active: boolean;
  has_access_token?: boolean;
  has_app_secret?: boolean;
  last_error_code?: string | null;
  last_error_at?: string | null;
  default_ai_agent_id?: string | null;
  is_principal?: boolean;
  templates_approved_count?: number | null;
  agents_count?: number;
  sequences_count?: number;
  conversations_30d_count?: number;
  created_at?: string;
}

/** Forma curta que vem junto do agente e do item de sequência. */
export interface WhatsappAccountRef {
  id: string;
  name: string;
  display_phone_number?: string | null;
  status?: WhatsappAccountStatus;
}

export interface WhatsappAccountList {
  data: WhatsappAccount[];
  limit: number;
  can_add: boolean;
}

export interface WhatsappAccountUpdate {
  name?: string;
  waba_id?: string;
  phone_number_id?: string;
  access_token?: string;
  app_secret?: string;
  default_ai_agent_id?: string | null;
}

export interface WhatsappAccountTestResult {
  connected: boolean;
  display_phone_number?: string;
  verified_name?: string;
  verified_at?: string;
}

export interface WhatsappAccountAgent {
  id: string;
  name: string;
  active: boolean;
  llm_model?: string | null;
  smsfunnel_ai_model_id?: string | null;
  /** Nome do Modelo do SMSFunnel (Essencial, Pro…) — é o que o cliente vê. */
  smsfunnel_ai_model_name?: string | null;
  is_default: boolean;
}

export interface WhatsappAccountSequence {
  id: string;
  campaign_id: string;
  campaign_name: string;
  position: number | null;
  template_name?: string | null;
  ai_agent_id?: string | null;
  ai_agent_name?: string | null;
  active: boolean;
}

/** Corpo do 409 ao excluir uma integração em uso. */
export interface WhatsappAccountInUse {
  message: string;
  agents: { id: string; name: string; active: boolean }[];
  sequences: WhatsappAccountSequence[];
}

@Injectable({ providedIn: 'root' })
export class WhatsappAccountsService {

  private base = `${environment.apiUrl}/whatsapp-accounts`;

  constructor(private http: HttpClient) { }

  /** Todas as integrações do cliente; quem escolhe número filtra por `status === 'connected'`. */
  list(): Observable<WhatsappAccountList> {
    return this.http.get<WhatsappAccountList>(this.base);
  }

  get(id: string): Observable<WhatsappAccount> {
    return this.http.get<WhatsappAccount>(`${this.base}/${id}`);
  }

  /** A tela mostra o 422 (nome repetido, limite) no próprio campo: sem o toast do interceptor. */
  create(name: string): Observable<WhatsappAccount> {
    return this.http.post<WhatsappAccount>(this.base, { name }, ownErrorToastOptions());
  }

  /** Idem: nome repetido e Phone Number ID de outra integração aparecem no campo. */
  update(id: string, payload: WhatsappAccountUpdate): Observable<WhatsappAccount> {
    return this.http.put<WhatsappAccount>(`${this.base}/${id}`, payload, ownErrorToastOptions());
  }

  // Daqui para baixo as telas dão o próprio aviso de erro (424 vira aviso na tela, 409 vira o
  // modal de bloqueio, status 0 sai uma vez por `toastOwnError`): sem o toast do interceptor.

  test(id: string): Observable<WhatsappAccountTestResult> {
    return this.http.post<WhatsappAccountTestResult>(`${this.base}/${id}/test`, {}, ownErrorToastOptions());
  }

  activate(id: string): Observable<WhatsappAccount> {
    return this.http.post<WhatsappAccount>(`${this.base}/${id}/activate`, {}, ownErrorToastOptions());
  }

  deactivate(id: string): Observable<WhatsappAccount> {
    return this.http.post<WhatsappAccount>(`${this.base}/${id}/deactivate`, {}, ownErrorToastOptions());
  }

  remove(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`, ownErrorToastOptions());
  }

  webhookInfo(): Observable<{ url: string; verify_token: string }> {
    return this.http.get<{ url: string; verify_token: string }>(`${this.base}/webhook-info`);
  }

  listTemplates(id: string): Observable<MetaTemplate[]> {
    return this.http.get<MetaTemplate[]>(`${this.base}/${id}/templates`, ownErrorToastOptions());
  }

  createTemplate(id: string, payload: CreateTemplatePayload): Observable<any> {
    return this.http.post<any>(`${this.base}/${id}/templates`, payload, ownErrorToastOptions());
  }

  deleteTemplate(id: string, name: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/templates/${encodeURIComponent(name)}`, ownErrorToastOptions());
  }

  listAgents(id: string): Observable<WhatsappAccountAgent[]> {
    return this.http.get<WhatsappAccountAgent[]>(`${this.base}/${id}/agents`, ownErrorToastOptions());
  }

  listSequences(id: string): Observable<WhatsappAccountSequence[]> {
    return this.http.get<WhatsappAccountSequence[]>(`${this.base}/${id}/sequences`, ownErrorToastOptions());
  }

  swapSequenceAgent(id: string, sequenceId: string, aiAgentId: string): Observable<{ id: string; ai_agent_id: string }> {
    return this.http.put<{ id: string; ai_agent_id: string }>(
      `${this.base}/${id}/sequences/${sequenceId}/agent`,
      { ai_agent_id: aiAgentId },
      ownErrorToastOptions(),
    );
  }
}
